import { useEffect, useMemo } from 'react'
import { MessageCircle } from 'lucide-react'
import { getChatAdapter } from '../../lib/chatAdapter'
import { isFeatureEnabled } from '../../lib/featureFlags'

const SCRIPT_ATTR = 'data-helin-chat'

/**
 * Renders the chat trigger when:
 *   - VITE_FEATURE_CHAT === 'true' (or runtime override)
 *   - VITE_CHAT_PROVIDER is set to a valid provider
 *   - the provider's required env (number/key/id) is present
 *
 * For SDK providers (Tidio/Intercom/Crisp/custom): inject script + render nothing
 * (the SDK draws its own widget). For WhatsApp: render a click-through button.
 */
export default function ChatLauncher() {
  const adapter = useMemo(() => getChatAdapter(), [])
  const featureOn = isFeatureEnabled('CHAT')

  useEffect(() => {
    if (!featureOn || !adapter.enabled || !adapter.scriptUrl) return
    if (document.querySelector(`script[${SCRIPT_ATTR}]`)) return

    if (adapter.initSnippet) {
      const initScript = document.createElement('script')
      initScript.setAttribute(SCRIPT_ATTR, 'init')
      initScript.text = adapter.initSnippet
      document.head.appendChild(initScript)
    }

    const tag = document.createElement('script')
    tag.setAttribute(SCRIPT_ATTR, 'main')
    tag.async = true
    tag.src = adapter.scriptUrl
    document.head.appendChild(tag)
  }, [adapter, featureOn])

  if (!featureOn || !adapter.enabled) return null
  if (!adapter.clickThroughUrl) return null // SDK providers render their own widget

  return (
    <a
      href={adapter.clickThroughUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={adapter.label}
      className="fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-gold text-ivory shadow-lg hover:shadow-gold-glow transition-shadow flex items-center justify-center"
    >
      <MessageCircle size={20} />
    </a>
  )
}
